import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { combineLatest, Observable, Subscription, of } from 'rxjs';
import { delay, map, startWith, distinctUntilChanged } from 'rxjs/operators';

import { PaxAmount } from '@fcom/dapi';
import { isPresent, LocalDate, unsubscribe } from '@fcom/core/utils';
import { NotificationTheme } from '@fcom/ui-components';
import { FinnairBoundItemWithLocation } from '@fcom/common-booking';
import { GaContext } from '@fcom/common/interfaces';
import { SelectionPhase } from '@fcom/common/interfaces/booking';
import { LanguageSelectorContext } from '@fcom/common/components/language-selector/language-selector.component';

import { LocationNames } from '../../interfaces/locations.interface';
import { AppConstants } from '../../../../app-constants';

const focus = (elemRef: ElementRef): void => {
  if (isPresent(elemRef) && isPresent(elemRef.nativeElement)) {
    elemRef.nativeElement.focus();
  }
};

@Component({
  selector: 'fin-ticket-selection-header',
  styleUrls: ['./ticket-selection-header.component.scss'],
  templateUrl: './ticket-selection-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketSelectionHeaderComponent implements OnInit, OnDestroy {
  readonly SelectionPhase = SelectionPhase;
  readonly C = AppConstants;
  readonly NotificationTheme = NotificationTheme;
  readonly TRACKING_CONTEXT = GaContext.FLIGHT_SELECTION;
  readonly BOUND_SELECTION_PHASE_INDEX = {
    [SelectionPhase.OUTBOUND]: 0,
    [SelectionPhase.INBOUND]: 1,
  };
  readonly LANGUAGE_SELECTOR_CONTEXT = LanguageSelectorContext.BOOKING;

  @Input()
  departureDate$: Observable<LocalDate>;
  @Input()
  returnDate$: Observable<LocalDate>;
  @Input()
  outbound$: Observable<FinnairBoundItemWithLocation>;
  @Input()
  inbound$: Observable<FinnairBoundItemWithLocation>;
  @Input()
  isOneWay$: Observable<boolean>;
  @Input()
  paxAmount$: Observable<PaxAmount>;
  @Input()
  currencyCode$: Observable<string>;
  @Input()
  selectionPhase$: Observable<SelectionPhase>;
  @Input()
  promoCode$: Observable<string>;
  @Input()
  enableNewLanguageSelector = false;
  @Input()
  tripLocations: LocationNames[];
  @Input()
  enableNewStepper = false;

  @Output()
  clickOutbound: EventEmitter<void> = new EventEmitter();
  @Output()
  clickInbound: EventEmitter<void> = new EventEmitter();

  boundSelection$: Observable<FinnairBoundItemWithLocation[]>;
  numberOfBoundText$ = of('');

  @ViewChild('outboundButton', { static: false })
  private outboundButton: ElementRef;
  @ViewChild('inboundButton', { static: false })
  private inboundButton: ElementRef;

  private subscription: Subscription;
  ngOnInit(): void {
    this.numberOfBoundText$ = this.selectionPhase$.pipe(
      distinctUntilChanged(),
      map((selectionPhase) => `${this.BOUND_SELECTION_PHASE_INDEX[selectionPhase] + 1}. `)
    );
    this.boundSelection$ = combineLatest([
      this.outbound$,
      this.inbound$.pipe(startWith(undefined as FinnairBoundItemWithLocation)),
    ]).pipe(map((bounds) => bounds.filter(Boolean)));

    this.subscription = this.selectionPhase$
      .pipe(
        delay(1) // Won't work with ContinueButton without delay
      )
      .subscribe((selectionPhase: SelectionPhase) => {
        if (selectionPhase === SelectionPhase.OUTBOUND) {
          focus(this.outboundButton);
        } else if (selectionPhase === SelectionPhase.INBOUND) {
          focus(this.inboundButton);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }
}
